import { useCallback } from "react";
import { useSearch } from "wouter";
import type { Node, ViewFilters } from "@melo/shared";
import { navigate } from "./nav";
import { store, type Tables } from "./store";
import { todayIso } from "./select";

/** URL keys for each filter, so a filtered view can be shared as a link. */
const KEYS = { milestone: "in", assignees: "who", labels: "label", statuses: "status", priorities: "priority", disciplines: "does", due: "due", text: "q" } as const;
type ListKey = "assignees" | "labels" | "statuses" | "priorities" | "disciplines";

export function readFilters(search: string): ViewFilters | null {
  const p = new URLSearchParams(search);
  if (![...Object.values(KEYS)].some((k) => p.has(k))) return null;
  const list = (k: string) => (p.get(k) ? p.get(k)!.split(",").filter(Boolean) : undefined);
  const due = p.get(KEYS.due);
  return {
    assignees: list(KEYS.assignees),
    labels: list(KEYS.labels),
    statuses: list(KEYS.statuses),
    priorities: list(KEYS.priorities),
    disciplines: list(KEYS.disciplines),
    due: due === "overdue" || due === "week" || due === "none" ? due : undefined,
    text: p.get(KEYS.text) ?? undefined,
    milestone: /^[0-9a-f-]{36}$/i.test(p.get(KEYS.milestone) ?? "") ? p.get(KEYS.milestone)! : undefined,
  };
}

/** How many narrowing filters are on, not counting the milestone scope or the title search. */
export function filterCount(f: ViewFilters): number {
  return (f.assignees?.length ?? 0) + (f.labels?.length ?? 0) + (f.statuses?.length ?? 0) + (f.priorities?.length ?? 0) + (f.disciplines?.length ?? 0) + (f.due ? 1 : 0);
}

export function writeFilters(f: ViewFilters | null) {
  const p = new URLSearchParams(window.location.search);
  for (const k of Object.values(KEYS)) p.delete(k);
  if (f) {
    for (const [name, key] of Object.entries(KEYS) as [keyof ViewFilters, string][]) {
      const v = f[name];
      if (Array.isArray(v) ? v.length : v) p.set(key, Array.isArray(v) ? v.join(",") : String(v));
    }
    // An explicitly empty filter set still marks the URL as filtered, so it overrides the view's saved filters.
    if (![...Object.values(KEYS)].some((k) => p.has(k))) p.set(KEYS.text, "");
  }
  const qs = p.toString();
  navigate(`${window.location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
}

export const isEmpty = (f: ViewFilters) => !Object.values(f).some((v) => (Array.isArray(v) ? v.length : v));
export const sameFilters = (a: ViewFilters, b: ViewFilters) => JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
function normalize(f: ViewFilters) {
  return Object.fromEntries(Object.entries(f).filter(([, v]) => (Array.isArray(v) ? v.length : v)).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => [k, Array.isArray(v) ? [...v].sort() : v]));
}

/** The filters in force: the URL's if it has any, otherwise the view's saved ones. */
export function useFilters(saved: ViewFilters): [ViewFilters, (f: ViewFilters | null) => void, boolean] {
  const search = useSearch();
  const fromUrl = readFilters(search);
  const set = useCallback((f: ViewFilters | null) => writeFilters(f), []);
  return [fromUrl ?? saved, set, fromUrl !== null];
}

export function toggleIn(f: ViewFilters, key: ListKey, value: string): ViewFilters {
  const cur = f[key] ?? [];
  return { ...f, [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
}

function weekEnd(today: string): string {
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() + (7 - ((d.getDay() + 6) % 7)) - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function matches(t: Tables, n: Node, f: ViewFilters): boolean {
  if (f.assignees?.length) {
    const me = store.me?.id;
    const want = f.assignees.map((a) => (a === "me" ? me : a));
    const none = want.includes("none") && n.assignees.length === 0;
    if (!none && !n.assignees.some((a) => want.includes(a.actorId))) return false;
  }
  if (f.labels?.length && !n.labels.some((l) => f.labels!.includes(l))) return false;
  if (f.statuses?.length) {
    const cat = t.kinds.get(n.kindId)?.statuses.find((s) => s.key === n.status)?.category;
    if (!f.statuses.includes(n.status) && !(cat && f.statuses.includes(`cat:${cat}`))) return false;
  }
  if (f.priorities?.length && !f.priorities.includes(String(n.fields.priority ?? "none"))) return false;
  if (f.disciplines?.length && !f.disciplines.includes(String(n.fields.discipline ?? "none"))) return false;
  if (f.due) {
    const today = todayIso();
    if (f.due === "none" && n.dueDate) return false;
    if (f.due === "overdue" && !(n.dueDate && n.dueDate < today)) return false;
    if (f.due === "week" && !(n.dueDate && n.dueDate >= today && n.dueDate <= weekEnd(today))) return false;
  }
  if (f.text?.trim()) {
    const q = f.text.trim().toLowerCase();
    if (!n.title.toLowerCase().includes(q) && !n.body.toLowerCase().includes(q)) return false;
  }
  return true;
}
